import parsePhoneNumber, { CountryCode as CountryCodeEnum, PhoneNumber } from 'libphonenumber-js';
import { InviteRow } from '@/types/adminInvites';
import { CountryCode } from '@/types/phoneCountryCodes';

// MM-42243: the bulk grid's PhoneNumberInput keeps a phone as two parts — its `phoneNumber` is the NATIONAL number and
// its `phoneCountryCode` is the dial code (a PhoneCountryCodes entry: ISO Code + dial Ext) — and the invite service
// likewise stores Phone and PhoneCountryCode separately. A CSV, though, may deliver a full international number
// ("+15037795680"), a dial code with a leading "+" ("+1"), or a bare national number with only the row's CountryCode
// to go on. These helpers bring every row into the component's shape at ingestion and resolve the exact country the
// editor should show, so opening a Phone Number cell presents the same country and formatting the component itself
// would produce.

function stripPlus(value: string | null | undefined): string {
  return (value ?? '').trim().replace(/^\+/, '');
}

// Parses the row's phone using every hint it carries: an E.164 phone stands alone; otherwise the dial code and/or the
// row's ISO CountryCode qualify the national number. Undefined when the phone cannot be parsed.
function parseRowPhone(row: InviteRow): PhoneNumber | undefined {
  const phone = row.phone?.trim();
  if (!phone)
    return undefined;

  if (phone.startsWith('+'))
    return parsePhoneNumber(phone);

  const ext = stripPlus(row.phoneCountryCode);
  if (ext !== '')
    return parsePhoneNumber(`+${ext}${phone}`);

  const iso = row.countryCode?.trim().toUpperCase();
  return iso ? parsePhoneNumber(phone, iso as CountryCodeEnum) : undefined;
}

// Normalizes one ingested row in place: the phone becomes the national number formatted for its country and
// PhoneCountryCode becomes the bare dial code (no "+"). A phone that cannot be parsed is left as-is (the operator can
// correct it in the grid) apart from dropping a "+" on its dial code.
export function normalizeInvitePhone(row: InviteRow): void {
  if (row.phoneCountryCode != null)
    row.phoneCountryCode = stripPlus(row.phoneCountryCode) || null;

  const parsed = parseRowPhone(row);
  if (!parsed)
    return;

  row.phoneCountryCode = parsed.countryCallingCode;
  row.phone = parsed.formatNational();
}

// The PhoneCountryCodes entry the phone editor should select for a row. Dial codes are shared (+1 covers the US,
// Canada and most of the Caribbean), so the country is resolved in order of certainty: the country libphonenumber
// identifies from the full number (by area code), then the row's own CountryCode when its dial code agrees, then the
// first country with that dial code. A row with no dial code falls back to its CountryCode.
export function resolvePhoneCountry(row: InviteRow, codes: CountryCode[]): CountryCode | undefined {
  const ext = stripPlus(row.phoneCountryCode);
  const iso = row.countryCode?.trim().toUpperCase() ?? '';

  const parsedIso = parseRowPhone(row)?.country;
  const byParsed = parsedIso ? codes.find(code => code.Code === parsedIso) : undefined;
  if (byParsed && (ext === '' || byParsed.Ext === ext))
    return byParsed;

  if (ext === '')
    return iso ? codes.find(code => code.Code === iso) : undefined;

  return codes.find(code => code.Code === iso && code.Ext === ext)
    ?? codes.find(code => code.Ext === ext);
}

// The idle (non-editing) text for a Phone Number cell: "+<dial code> <national number>", or the bare number when no
// dial code is known.
export function displayInvitePhone(row: InviteRow): string {
  const phone = row.phone?.trim() ?? '';
  if (phone === '')
    return '';
  const ext = stripPlus(row.phoneCountryCode);
  return ext ? `+${ext} ${phone}` : phone;
}
